// lib/qqq/method-match.js
// ═══════════════════════════════════════════════════════════
// METHOD MATCH — Layer 5 (Proactive Alerts)
// ═══════════════════════════════════════════════════════════
// Reads the latest Method Sheet's observations + signal hierarchy
// and scores how much TODAY's captured session data matches
// each one. Returns an array of alerts surfaced in Phase 1
// (Pre-Open Prep) so Tim sees the signal in his face when he
// opens the page, not buried in Phase 5.
//
// "Today is HIGH match for Direction Read #3 — 78% CALL win rate"
// "⚠ Today matches a documented LOSS pattern — reduce size"
//
// Pure module — no DB, no fetchers. Caller passes:
//   - the latest method sheet (or null)
//   - today's session data
//   - today's auto + user drivers
// Returns array of alerts sorted by severity desc.
// ═══════════════════════════════════════════════════════════

import { suggestDrivers } from './driver-suggest';

// ═══════════════════════════════════════════════════════════
// DECAY EXPORTS (CC-6 — 2026-05-12)
// ═══════════════════════════════════════════════════════════
// findMethodMatches already produces decay_warning alerts. But the
// Bullish X/10 panel (synthesis.js scoreSignals) needs to KNOW
// about decay flags to adjust signal weights — otherwise "Above PDH"
// gets counted as full bullish even when historically decayed 75→64%.
//
// These exports let scoreSignals + future consumers subscribe to
// decay info independently of the alerts pipeline.
// ═══════════════════════════════════════════════════════════

/**
 * Return raw decay watch entries from the method sheet.
 *
 * @param {object|null} methodSheet
 * @returns {Array<{ signal, was_pct, now_pct, samples, note }>}
 */
export function getDecayedSignals(methodSheet) {
  if (!methodSheet) return [];
  const decay = Array.isArray(methodSheet.decay_watch) ? methodSheet.decay_watch : [];
  return decay.map((d) => ({
    signal:    d.signal,
    was_pct:   d.was_pct,
    now_pct:   d.now_pct,
    samples:   d.samples,
    note:      d.note,
  }));
}

// Match table: signal key in scoreSignals() → method-sheet decay-watch
// signal-name fragments. If any fragment appears (case-insensitive)
// in the decay entry's `signal` field, the signal is considered
// decayed.
const SIGNAL_KEY_TO_DECAY_FRAGMENTS = {
  pdh:      ['above pdh', 'pdh break', 'prior day high'],
  gamma:    ['gamma positive', 'gamma regime'],
  vix_term: ['contango', 'vix term', 'vix contango'],
  pcr:      ['pcr bullish', 'pcr', 'put/call'],
  tech:     ['tech leading', 'tech lead'],
  dollar:   ['dollar weak', 'dxy weak'],
  yield:    ['curve positive', 'yield curve'],
  window:   ['clean window'],
  credit:   ['credit risk-on', 'credit spread'],
  breadth:  ['breadth', 'strong breadth'],
};

/**
 * Find the decay entry (if any) that applies to a scored signal.
 *
 * @param {string} signalKey — synthesis.scoreSignals() signal key
 *                             (e.g. 'pdh', 'gamma', 'tech', ...)
 * @param {Array} decayedSignals — output of getDecayedSignals()
 * @returns {object|null} matching decay entry or null
 */
export function matchDecayForSignalKey(signalKey, decayedSignals) {
  if (!signalKey || !Array.isArray(decayedSignals) || decayedSignals.length === 0) return null;
  const fragments = SIGNAL_KEY_TO_DECAY_FRAGMENTS[signalKey];
  if (!fragments) return null;
  for (const entry of decayedSignals) {
    const haystack = String(entry.signal || '').toLowerCase();
    for (const frag of fragments) {
      if (haystack.includes(frag)) return entry;
    }
  }
  return null;
}

/**
 * Top-level — return alerts where today matches a method
 * sheet observation.
 *
 * @param methodSheet — latest qqq_method_sheets row (or null)
 * @param sessionData — live session payload (data.prices, .environment, etc.)
 * @param userDrivers — array of move_drivers (user-tagged)
 * @param autoDrivers — array of auto_drivers (Shift-observed)
 * @returns array of alerts, sorted by severity desc
 */
export function findMethodMatches(methodSheet, sessionData, userDrivers, autoDrivers) {
  if (!methodSheet) return [];
  const out = [];

  // Build a feature snapshot of today
  const todayFeatures = buildTodayFeatures(sessionData, userDrivers, autoDrivers);

  // ─── Match against Stage 1 directional signals (entry_rules) ─
  const dirSignals = Array.isArray(methodSheet.entry_rules) ? methodSheet.entry_rules : [];
  for (let i = 0; i < dirSignals.length; i++) {
    const sig = dirSignals[i];
    if (!sig?.rule) continue;
    const matchScore = scoreObservationMatch(sig.rule, todayFeatures);
    if (matchScore.score >= 0.5) {
      out.push({
        kind:     'direction_match',
        severity: matchScore.score >= 0.75 ? 'high' : 'medium',
        title:    `Today matches Direction Read #${i + 1}`,
        observation: sig.rule,
        evidence: sig.evidence,
        why:      sig.why,
        match_score: matchScore.score,
        match_features: matchScore.matched,
      });
    }
  }

  // ─── Match against Signal Hierarchy ───
  const sigs = Array.isArray(methodSheet.signal_hierarchy) ? methodSheet.signal_hierarchy : [];
  const activeHighSignals = sigs.filter((s) => {
    if (s.weight !== 'high') return false;
    return signalIsActiveToday(s.signal, todayFeatures);
  });
  if (activeHighSignals.length >= 2) {
    out.push({
      kind:     'signal_stack',
      severity: 'high',
      title:    `${activeHighSignals.length} HIGH-weight signals firing today`,
      signals:  activeHighSignals.map((s) => `${s.signal} (${s.hit_rate_pct}%)`),
      why:      `When 2+ HIGH-weight signals fire together, your historical hit rate clusters above 70%. This is a setup-confidence stack.`,
    });
  }

  // ─── Match against Decay Watch (warn if today depends on a decaying signal) ──
  const decay = Array.isArray(methodSheet.decay_watch) ? methodSheet.decay_watch : [];
  for (const d of decay) {
    if (signalIsActiveToday(d.signal, todayFeatures)) {
      out.push({
        kind:     'decay_warning',
        severity: 'medium',
        title:    `⚠ ${d.signal} firing — but it's decaying`,
        observation: `${d.was_pct}% → ${d.now_pct}% over ${d.samples} samples`,
        why:      d.note || `This signal was reliable but recent samples show degradation. Discount its weight in your decision today.`,
      });
    }
  }

  // ─── Match the Sharpening Focus (always surface — it's the
  //     ONE thing for the week) ───
  if (methodSheet.sharpening_focus) {
    out.push({
      kind:     'sharpening',
      severity: 'low',
      title:    `⚡ This week's focus`,
      observation: methodSheet.sharpening_focus,
      why:      'From your latest Method Sheet — measure this on every trade today.',
    });
  }

  // Sort by severity desc (high → medium → low)
  const sevRank = { high: 3, medium: 2, low: 1 };
  out.sort((a, b) => (sevRank[b.severity] || 0) - (sevRank[a.severity] || 0));

  return out;
}

// ─── Build a flat feature snapshot of today ────────────────
// Produces a Set of feature keys + small key→value map so the
// matcher can cheaply check "does today's session contain X feature".
function buildTodayFeatures(sessionData, userDrivers, autoDrivers) {
  const features = new Set();
  const values = {};

  // Pull suggestions from driver-suggest engine (this is what the
  // auto-driver capture produces) — gives us a normalized view of
  // today's notable conditions.
  const suggestions = (() => {
    try { return suggestDrivers(sessionData) || []; }
    catch { return []; }
  })();

  for (const s of suggestions) {
    const lc = (s.driver || '').toLowerCase();
    features.add(lc);
    // Common keyword fingerprints for substring matching
    if (lc.includes('tech leading'))     features.add('tech_leading');
    if (lc.includes('gamma'))            features.add('gamma_active');
    if (lc.includes('above pdh'))        features.add('above_pdh');
    if (lc.includes('below pdl'))        features.add('below_pdl');
    if (lc.includes('contango'))         features.add('vix_contango');
    if (lc.includes('backwardation'))    features.add('vix_backwardation');
    if (lc.includes('overbought'))       features.add('rsi_overbought');
    if (lc.includes('oversold'))         features.add('rsi_oversold');
    if (lc.includes('fomc'))             features.add('fomc_today');
    if (lc.includes('earnings'))         features.add('earnings_today');
    if (lc.includes('marubozu'))         features.add('marubozu');
    if (lc.includes('hammer'))           features.add('hammer');
  }

  // Add user-tagged driver categories
  for (const d of userDrivers || []) {
    if (d.category) features.add(`user_${d.category}`);
  }
  for (const d of autoDrivers || []) {
    if (d.category) features.add(`auto_${d.category}`);
  }

  return { features, values };
}

// ─── Score how well today's features match an observation ──
// Observation text → tokenize → check feature overlap.
// Returns { score 0-1, matched: [feature names] }.
function scoreObservationMatch(observationText, todayFeatures) {
  const text = String(observationText || '').toLowerCase();
  const matched = [];
  const checks = [
    { keyword: 'tech leading',    feature: 'tech_leading' },
    { keyword: 'gamma',           feature: 'gamma_active' },
    { keyword: 'above pdh',       feature: 'above_pdh' },
    { keyword: 'below pdl',       feature: 'below_pdl' },
    { keyword: 'contango',        feature: 'vix_contango' },
    { keyword: 'backwardation',   feature: 'vix_backwardation' },
    { keyword: 'overbought',      feature: 'rsi_overbought' },
    { keyword: 'oversold',        feature: 'rsi_oversold' },
    { keyword: 'fomc',            feature: 'fomc_today' },
    { keyword: 'earnings',        feature: 'earnings_today' },
    { keyword: 'marubozu',        feature: 'marubozu' },
    { keyword: 'hammer',          feature: 'hammer' },
  ];
  let totalChecks = 0;
  let totalMatches = 0;
  for (const c of checks) {
    if (!text.includes(c.keyword)) continue;
    totalChecks += 1;
    if (todayFeatures.features.has(c.feature)) {
      totalMatches += 1;
      matched.push(c.feature);
    }
  }
  if (totalChecks === 0) return { score: 0, matched };
  return { score: totalMatches / totalChecks, matched };
}

// Quick lookup: is a named signal active today based on the
// captured features?
function signalIsActiveToday(signalName, todayFeatures) {
  const lc = String(signalName || '').toLowerCase();
  if (lc.includes('tech leading'))       return todayFeatures.features.has('tech_leading');
  if (lc.includes('gamma positive'))     return todayFeatures.features.has('gamma_active');
  if (lc.includes('above pdh'))          return todayFeatures.features.has('above_pdh');
  if (lc.includes('contango'))           return todayFeatures.features.has('vix_contango');
  if (lc.includes('backwardation'))      return todayFeatures.features.has('vix_backwardation');
  if (lc.includes('rsi overbought'))     return todayFeatures.features.has('rsi_overbought');
  if (lc.includes('rsi oversold'))       return todayFeatures.features.has('rsi_oversold');
  if (lc.includes('fomc'))               return todayFeatures.features.has('fomc_today');
  return false;
}
